import clsx from 'clsx';
import { StaticImage } from 'gatsby-plugin-image';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { EffectCoverflow, Navigation, Autoplay } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import Link from 'components/shared/link';
import MainContext from 'context/main';

import cardPointersLogo from './images/card-pointers-logo.svg';
import openaiLogo from './images/openai-logo.svg';
import photoRoomLogo from './images/photoroom-logo.svg';
import pixeryLogo from './images/pixery-logo.svg';
import tappingSolutionLogo from './images/the-tapping-solution-logo.svg';
import vscoLogo from './images/vsco-logo.svg';

import 'swiper/css';
import 'swiper/css/effect-coverflow';
import 'swiper/css/navigation';
import 'swiper/css/autoplay';
import './slider.css';

const logos = {
  vsco: { src: vscoLogo, width: 116, height: 44 },
  photoroom: { src: photoRoomLogo, width: 198, height: 44 },
  chatgpt: { src: openaiLogo, width: 127, height: 36 },
  cardpointers: { src: cardPointersLogo, width: 184, height: 44 },
  thetappingsolution: { src: tappingSolutionLogo, width: 235, height: 44 },
  pixery: { src: pixeryLogo, width: 103, height: 44 },
};

const authors = {
  essabhoy: {
    avatar: (
      <StaticImage
        className="rounded-full"
        imgClassName="rounded-full"
        src="./images/shaheen-essabhoy.jpg"
        alt="Shaheen Essabhoy"
        width={48}
        height={48}
      />
    ),
    name: 'Shaheen Essabhoy',
  },
  lemarié: {
    avatar: (
      <StaticImage
        className="rounded-full"
        imgClassName="rounded-full"
        src="./images/olivier-lemarie.jpg"
        alt="Olivier Lemarie"
        width={48}
        height={48}
      />
    ),
    name: 'Olivier Lemarié',
  },
  crouvisier: {
    avatar: (
      <StaticImage
        className="rounded-full"
        imgClassName="rounded-full"
        src="./images/emmanuel-crouvisier.jpg"
        alt="Emmanuel Crouvisier"
        width={48}
        height={48}
      />
    ),
    name: 'Emmanuel Crouvisier',
  },
  plummer: {
    avatar: (
      <StaticImage
        className="rounded-full"
        imgClassName="rounded-full"
        src="./images/kelly-plummer.jpg"
        alt="Kelly Plummer"
        width={48}
        height={48}
      />
    ),
    name: 'Kelly Plummer',
  },
  ugur: {
    avatar: (
      <StaticImage
        className="rounded-full"
        imgClassName="rounded-full"
        src="./images/kemal-ugur.jpg"
        alt="Kemal Ugur"
        width={48}
        height={48}
      />
    ),
    name: 'Kemal Ugur',
  },
  conlon: {
    avatar: (
      <StaticImage
        className="rounded-full"
        imgClassName="rounded-full"
        src="./images/sara-conlon.jpg"
        alt="Sara Conlon"
        width={48}
        height={48}
      />
    ),
    name: 'Sara Conlon',
  },
};

const TestimonialsSlider = ({ theme, title, testimonials }) => {
  const shortMode = testimonials.length < 4;
  const [sliderRef, isSliderInView] = useInView({ triggerOnce: true, threshold: 0.4 });
  const [swiper, setSwiper] = useState();
  const [activeIndex, setActiveIndex] = useState(0);
  const {
    language: { locale },
  } = useContext(MainContext);

  useEffect(() => {
    isSliderInView && swiper?.autoplay.start();
  }, [isSliderInView, swiper?.autoplay]);

  const handleSwiper = (swiperApi) => {
    setSwiper(swiperApi);
  };

  const handleSlideChange = (index) => {
    if (swiper) {
      swiper.slideToLoop(index);
      setActiveIndex(index);
    }
  };

  return (
    <section
      className={clsx(
        'testimonials safe-paddings overflow-hidden py-[120px] xl:py-24 lg:py-20 md:py-16',
        theme === 'gray' ? 'bg-new-gray-2' : 'bg-white'
      )}
    >
      <div className="container">
        <h2 className="mx-auto max-w-[800px] text-center text-[56px] font-medium leading-denser tracking-tight-4 text-new-black xl:max-w-[632px] xl:text-[44px] lg:max-w-[500px] lg:text-[36px] md:text-[32px]">
          {title}
        </h2>
        <div className="flex flex-col" ref={sliderRef}>
          <Swiper
            className="w-full"
            id="testimonials-swiper"
            autoplay={
              isSliderInView
                ? {
                    delay: 8000,
                    disableOnInteraction: true,
                  }
                : false
            }
            modules={[EffectCoverflow, Navigation, Autoplay]}
            slidesPerView="auto"
            coverflowEffect={{
              rotate: 0,
              depth: 240,
              slideShadows: false,
            }}
            breakpoints={{
              1280: {
                coverflowEffect: {
                  stretch: 802.5,
                },
              },
              1024: {
                coverflowEffect: {
                  stretch: 636,
                },
              },
              768: {
                coverflowEffect: {
                  stretch: 482,
                },
              },
            }}
            effect="coverflow"
            centeredSlides
            grabCursor
            navigation
            loop
            onSwiper={handleSwiper}
            onRealIndexChange={(swiper) => setActiveIndex(swiper?.realIndex)}
          >
            {testimonials.map(
              (
                { title, description, logo, url, testimonial, author, authorRole, stats1, stats2 },
                index
              ) => {
                const authorData = authors[author];

                return (
                  <SwiperSlide
                    className="flex overflow-hidden rounded-2xl border border-[#D6DBF5] bg-white md:flex-col"
                    key={index}
                    tag="div"
                  >
                    <figure className="flex w-full flex-col pb-14 pl-[72px] pr-12 pt-12 xl:pb-9 xl:pl-14 xl:pr-4 xl:pt-9 lg:min-w-[434px] lg:p-9 lg:pr-[18px] md:h-[358px] md:min-w-full md:p-6">
                      <div className="flex items-center gap-x-6 xl:gap-x-[18px] lg:gap-x-4">
                        <img
                          className="xl:h-16 xl:w-16 lg:h-14 lg:w-14"
                          src={logo.url}
                          alt={title}
                          width={80}
                          height={80}
                          loading="lazy"
                        />
                        <div className="flex flex-col">
                          <h3 className="whitespace-nowrap text-[24px] font-bold leading-tight text-new-black xl:text-[20px] lg:text-[18px]">
                            {title}
                          </h3>
                          <span className="mt-1.5 text-[18px] leading-tight text-new-gray-1 xl:mt-1 xl:text-[16px] lg:text-[14px]">
                            {description}
                          </span>
                        </div>
                      </div>

                      <blockquote className="mb-[72px] mt-[30px] min-w-[607px] xl:mb-[60px] xl:mt-7 xl:min-w-0 xl:max-w-[494px] lg:mt-5 md:mb-0 md:mt-4">
                        <p className="text-[22px] font-light leading-snug text-new-gray-1 xl:text-[18px] lg:text-[16px]">
                          “{testimonial}”
                        </p>
                      </blockquote>
                      <figcaption className="mt-auto flex gap-x-4 xl:gap-x-3.5 lg:gap-x-3.5">
                        <div className="xl:h-11 xl:w-11 lg:h-10 lg:w-10">{authorData.avatar}</div>
                        <div className="flex flex-col">
                          <span className="text-[18px] font-medium leading-tight text-new-black xl:text-[16px] lg:text-[14px]">
                            {authorData.name}
                          </span>
                          {authorRole && (
                            <span className="text-[18px] font-light leading-tight text-[#6C7693] xl:text-[16px] lg:text-[14px]">
                              {authorRole}
                            </span>
                          )}
                        </div>
                      </figcaption>
                    </figure>
                    <div className="flex min-w-[279px] flex-col bg-new-gray-2 px-12 pb-[60px] pt-[58px] xl:min-w-[222px] xl:pb-10 xl:pl-8 xl:pr-4 xl:pt-9 lg:min-w-[168px] lg:px-5 md:min-h-[219px] md:py-6">
                      <div className="flex flex-col gap-y-5 xl:gap-y-[18px] lg:gap-y-6 md:gap-y-3">
                        {stats1 && (
                          <div className="flex flex-col">
                            <span className="font-sans2 text-5xl font-light leading-snug tracking-tight-4 text-new-primary-1 xl:text-[40px] lg:text-[32px]">
                              {stats1.split('\n')[0]}
                            </span>
                            <span className="-mt-1 text-base font-light leading-tight text-new-gray-1 xl:-mt-1 lg:text-[14px]">
                              {stats1.split('\n')[1]}
                            </span>
                          </div>
                        )}
                        {stats2 && (
                          <div className="flex flex-col">
                            <span className="font-sans2 text-5xl font-light leading-snug tracking-tight-4 text-secondary-2 xl:text-[40px] lg:text-[32px]">
                              {stats2.split('\n')[0]}
                            </span>
                            <span className="-mt-1 text-base font-light leading-tight text-new-gray-1 xl:-mt-1 lg:text-[14px]">
                              {stats2.split('\n')[1]}
                            </span>
                          </div>
                        )}
                      </div>
                      <Link
                        className="z-10 mt-auto"
                        to={url}
                        size="md"
                        theme="secondary-2"
                        withArrow
                      >
                        {locale === 'ja' ? '導入事例はこちら' : 'Read case study'}
                      </Link>
                    </div>
                  </SwiperSlide>
                );
              }
            )}
          </Swiper>
          <div
            className={clsx(
              'relative flex items-center justify-between gap-2 md:hidden',
              shortMode ? 'mx-auto gap-2' : 'w-full gap-6',
              'before:absolute before:inset-x-0 before:top-0 before:h-[3px] before:bg-new-gray-1/5'
            )}
          >
            {testimonials.map(({ title }, index) => {
              const company = title.toLowerCase().replace(/\s/g, '');
              const { width, height, src } = logos[company];
              const isActive = activeIndex === index;

              return (
                <button
                  className={clsx(
                    'relative z-10 flex h-20 items-center justify-center pt-9 lg:pt-6',
                    shortMode && 'w-[208px] lg:w-[154px]',
                    'before:absolute before:inset-x-0 before:top-0 before:h-[3px] before:rounded-full before:bg-new-gray-1',
                    'before:transition-opacity before:duration-200',
                    !isActive && 'before:opacity-0'
                  )}
                  type="button"
                  key={index}
                  onClick={() => handleSlideChange(index)}
                >
                  <span className="sr-only">{title}</span>
                  <img
                    className={clsx(
                      'h-9 w-auto',
                      shortMode ? 'lg:h-7' : 'xl:h-auto xl:w-full',
                      !isActive && 'opacity-50 grayscale'
                    )}
                    src={src}
                    width={width}
                    height={height}
                    alt={`${title} logo`}
                    loading="lazy"
                  />
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

TestimonialsSlider.propTypes = {
  theme: PropTypes.oneOf(['white', 'gray']).isRequired,
  title: PropTypes.string.isRequired,
  testimonials: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      logo: PropTypes.shape({
        url: PropTypes.string,
      }).isRequired,
      url: PropTypes.string.isRequired,
      testimonial: PropTypes.string.isRequired,
      author: PropTypes.oneOf(Object.keys(authors)).isRequired,
      authorRole: PropTypes.string,
      stats1: PropTypes.string,
      stats2: PropTypes.string,
    })
  ).isRequired,
};

export default TestimonialsSlider;
